import { Header } from "../components/promill/Header";
import { Footer } from "../components/promill/Footer";
import { Link } from "react-router-dom";
import {
    Leaf, Zap, ShieldCheck, Sprout,
    Wheat, FlaskConical, Beaker, Recycle,
    Activity, Heart, Users, Globe,
    ArrowRight, ChefHat, Salad, Clock,
} from "lucide-react";
import { WHATSAPP_URL } from "../data/promill";

const HERO_ABOUT = "https://images.unsplash.com/photo-1490645935967-10de6ba17061?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const PILLS = [
    { icon: Wheat, title: "Millet First", sub: "Always" },
    { icon: Zap, title: "High Protein", sub: "& Fibre" },
    { icon: Leaf, title: "Clean &", sub: "Wholesome" },
    { icon: Sprout, title: "Sustainable", sub: "By Choice" },
];

const PURPOSE = [
    { icon: Wheat, title: "100%", desc: "Millet Based Goodness" },
    { icon: FlaskConical, title: "No", desc: "Refined Flour · No Maida" },
    { icon: Beaker, title: "No", desc: "Artificial Preservatives or Additives" },
    { icon: Recycle, title: "Sustainable", desc: "Ingredients, Packaging & Practices" },
];

const MILLET_BENEFITS = [
    { icon: Activity, title: "Nutrient Rich", desc: "Loaded with protein, fibre, vitamins & minerals." },
    { icon: Heart, title: "Better For You", desc: "Low GI, gluten-friendly and easy to digest." },
    { icon: Users, title: "Better For Farmers", desc: "Supports local farming and rural communities." },
    { icon: Globe, title: "Better For Planet", desc: "Millets need less water and grow naturally." },
];

const MODERN_INDIA = [
    { icon: Zap, title: "High Protein Meals", desc: "Fuel your day with balanced nutrition." },
    { icon: Salad, title: "Clean Ingredients", desc: "Simple, honest and recognisable." },
    { icon: ChefHat, title: "Chef Crafted", desc: "Thoughtfully created for great taste." },
    { icon: Clock, title: "Everyday Food", desc: "Perfect for work, workouts & weekends." },
];

const AboutPage = () => (
    <div data-testid="about-page" className="bg-pm-cream min-h-screen">
        <Header />

        {/* HERO */}
        <section className="relative bg-pm-cream pt-10 md:pt-16 pb-14 md:pb-20 overflow-hidden">
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
                <div>
                    <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-4">
                        About Us
                    </span>
                    <h1 data-testid="about-headline" className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-[-0.02em] text-pm-black leading-[1.02]">
                        Good food.<br />
                        <span className="text-pm-green">Real impact.</span>
                    </h1>
                    <p className="mt-5 text-base md:text-lg text-pm-black/70 leading-relaxed max-w-lg">
                        ProMill is a modern food brand built on the power of millets. We create high-protein,
                        nutrient-rich meals that are delicious, convenient and crafted for everyday well-being.
                        Better for you. Better for farmers. Better for the planet.
                    </p>

                    <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-5">
                        {PILLS.map((p, i) => (
                            <div key={p.title} data-testid={`about-pill-${i}`} className="flex flex-col items-center text-center">
                                <div className="h-12 w-12 rounded-full bg-pm-yellow/20 flex items-center justify-center mb-3">
                                    <p.icon className="w-5 h-5 text-pm-green" strokeWidth={1.7} />
                                </div>
                                <div className="text-sm font-bold text-pm-black leading-tight">{p.title}</div>
                                <div className="text-xs text-pm-black/55 leading-tight mt-0.5">{p.sub}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="relative">
                    <div aria-hidden className="absolute -inset-3 bg-pm-yellow rounded-[28px] -rotate-2" />
                    <img
                        src={HERO_ABOUT}
                        alt="ProMill fresh meal"
                        className="relative w-full aspect-[5/4] object-cover rounded-2xl border border-pm-black/10 shadow-lg"
                    />
                </div>
            </div>
        </section>

        {/* OUR STORY */}
        <section className="bg-white py-16 md:py-24 border-y border-pm-black/5">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
                <div>
                    <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-4">
                        Our Story
                    </span>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.02em] text-pm-black leading-[1.02]">
                        Rooted in purpose.<br />
                        <span className="text-pm-green">Driven by impact.</span>
                    </h2>
                    <div className="mt-6 space-y-4 text-pm-black/70 leading-relaxed max-w-lg">
                        <p>ProMill was born from a simple belief — that food can fuel your body, support local farmers and protect our planet, all at once.</p>
                        <p>Millets are ancient grains with incredible potential. We bring them to your bowl in a way that fits your modern lifestyle.</p>
                        <p>We're not just another food brand. We're a movement towards better living and <span className="font-bold text-pm-black">a better tomorrow.</span></p>
                    </div>
                </div>

                <div className="rounded-3xl bg-pm-yellow/15 border border-pm-yellow/40 p-6 md:p-10 grid grid-cols-2 gap-6">
                    {PURPOSE.map((p, i) => (
                        <div key={i} data-testid={`purpose-${i}`} className="flex flex-col">
                            <div className="h-14 w-14 rounded-full bg-white flex items-center justify-center mb-3 border border-pm-black/8">
                                <p.icon className="w-6 h-6 text-pm-green" strokeWidth={1.7} />
                            </div>
                            <div className="text-2xl font-extrabold text-pm-black">{p.title}</div>
                            <div className="mt-1 text-sm text-pm-black/65 leading-snug">{p.desc}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* WHY MILLETS */}
        <section className="bg-pm-green text-white py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <span className="text-pm-yellow font-bold tracking-[0.25em] uppercase text-[11px]">
                        Why Millets?
                    </span>
                    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.02em] leading-tight">
                        Small grain. <span className="text-pm-yellow">Big benefits.</span>
                    </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {MILLET_BENEFITS.map((b, i) => (
                        <div key={i} data-testid={`millet-benefit-${i}`} className="text-center px-4">
                            <div className="h-16 w-16 rounded-full bg-white/10 border border-pm-yellow/40 flex items-center justify-center mx-auto mb-4">
                                <b.icon className="w-8 h-8 text-pm-yellow" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-lg font-extrabold">{b.title}</h3>
                            <p className="mt-2 text-sm text-white/80 leading-relaxed">{b.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* MADE FOR MODERN INDIA */}
        <section className="bg-pm-cream py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
                <div>
                    <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-4">
                        Made For Modern India
                    </span>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.02em] text-pm-black leading-[1.02]">
                        Healthy eating,<br />
                        <span className="text-pm-green">made easy.</span>
                    </h2>
                    <p className="mt-5 text-pm-black/70 leading-relaxed max-w-lg">
                        We know life moves fast. That's why our meals are designed to be quick, tasty and
                        truly nourishing. No compromises. Just real food for real life.
                    </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {MODERN_INDIA.map((m, i) => (
                        <div key={i} data-testid={`modern-${i}`} className="bg-white rounded-2xl p-5 border border-pm-black/8 flex gap-4 items-start card-lift">
                            <div className="h-12 w-12 shrink-0 rounded-full bg-pm-yellow/20 flex items-center justify-center">
                                <m.icon className="w-5 h-5 text-pm-green" />
                            </div>
                            <div>
                                <h3 className="text-base font-extrabold text-pm-black">{m.title}</h3>
                                <p className="mt-1 text-sm text-pm-black/60 leading-relaxed">{m.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* OUR PROMISE */}
        <section className="bg-pm-greenBright/15 py-16 md:py-24">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
                <div>
                    <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-4">
                        Our Promise
                    </span>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.02em] text-pm-black leading-[1.02]">
                        Real food.<br />
                        <span className="text-pm-orange">Real change.</span>
                    </h2>
                </div>
                <div className="text-pm-black/70 leading-relaxed">
                    <p className="text-base md:text-lg">
                        Every bowl you eat supports a healthier you, empowers local farmers and builds a more sustainable future.
                    </p>
                    <p className="mt-5 text-lg md:text-xl font-extrabold text-pm-black">
                        Good food today. Better world tomorrow.
                    </p>
                </div>
            </div>
        </section>

        {/* FUTURE / CTA */}
        <section className="relative overflow-hidden bg-[#1a2318] py-16 md:py-24">
            <div aria-hidden className="absolute inset-0 opacity-30" style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1500937386664-56d1dfef3854?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                mixBlendMode: "overlay",
            }} />
            <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#1a2318]/95 via-[#1a2318]/75 to-transparent" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center text-white">
                <div>
                    <span className="text-pm-yellow font-bold tracking-[0.25em] uppercase text-[11px]">
                        The Future We Are Building
                    </span>
                    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.02em] leading-[1.02]">
                        More bowls.<br />
                        <span className="text-pm-yellow">More impact.</span>
                    </h2>
                </div>
                <div>
                    <p className="text-white/85 text-base md:text-lg leading-relaxed">
                        We're just getting started. Our goal is to make millet-based meals accessible across
                        India and beyond — one bowl at a time.
                    </p>
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="about-enquire-cta"
                        className="hidden"
                    >
                        Enquire Now
                        <ArrowRight className="w-4 h-4" />
                    </a>
                </div>
            </div>
        </section>

        <Footer />
    </div>
);

export default AboutPage;
